import { trackEvent } from "./ga-events";
import { attachPlaceTypeahead, attachUseMyLocation } from "./concierge-typeahead";

/* Activity Concierge — wizard overlay on FamilyHQ guide pages.
   Custom <aside> overlay (not a Bootstrap modal). AJAX-submits to /activity-concierge/submit with
   X-Requested-With and no antiforgery token (guide pages are CDN-cached). Response is intentionally
   non-revealing: success → "check your inbox" panel; otherwise an inline status message. */
(function () {
  "use strict";

  var root = document.querySelector("[data-fhq-concierge]");
  if (!root || root.dataset.initialized === "true") return;
  root.dataset.initialized = "true";

  var SUBMITTED_KEY = "fhq_concierge_submitted";
  var AUTOSHOWN_KEY = "fhq_concierge_autoshown";
  var DRAFT_KEY = "fhq_concierge_draft";
  var DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
  var analyticsSource = root.getAttribute("data-analytics-source") || "familyhq_concierge";
  var dialog = root.querySelector(".fhq-concierge__dialog");
  var form = root.querySelector("[data-fhq-concierge-form]");
  var statusEl = root.querySelector("[data-fhq-concierge-status]");
  var donePanel = root.querySelector("[data-fhq-concierge-done]");
  var doneText = root.querySelector("[data-fhq-concierge-done-text]");
  var resendBtn = root.querySelector("[data-fhq-concierge-resend]");
  var resendStatus = root.querySelector("[data-fhq-concierge-resend-status]");
  var upsellPanel = root.querySelector("[data-fhq-concierge-upsell]");
  var emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var lastFocused = null;

  var steps = Array.prototype.slice.call(root.querySelectorAll(".fhq-concierge-step"))
    .sort(function (a, b) { return (a.dataset.step | 0) - (b.dataset.step | 0); });
  var current = 0;

  var backBtn = root.querySelector("[data-fhq-concierge-back]");
  var nextBtn = root.querySelector("[data-fhq-concierge-next]");
  var submitBtn = root.querySelector("[data-fhq-concierge-submit]");
  var progressFill = root.querySelector("[data-fhq-concierge-progress]");
  var stepCount = root.querySelector("[data-fhq-concierge-step-count]");

  function storageGet(k) { try { return window.localStorage.getItem(k); } catch (_) { return null; } }
  function storageSet(k, v) { try { window.localStorage.setItem(k, v); } catch (_) { } }
  function storageRemove(k) { try { window.localStorage.removeItem(k); } catch (_) { } }

  // Resume: persist the visitor's in-progress answers (+ current step) so closing/reopening doesn't lose them.
  function saveDraft() {
    if (!form) return;
    var fields = {};
    Array.prototype.forEach.call(form.elements, function (el) {
      if (!el.name || el.type === "hidden" || el.type === "submit" || el.type === "button") return;
      if (el.type === "checkbox" || el.type === "radio") {
        if (el.checked) (fields[el.name] = fields[el.name] || []).push(el.value);
      } else {
        fields[el.name] = el.value;
      }
    });
    storageSet(DRAFT_KEY, JSON.stringify({ savedAt: Date.now(), step: current, fields: fields }));
  }
  // Returns the step to resume on (0 when there's no usable draft). Restores field values into the form.
  function restoreDraft() {
    if (!form) return 0;
    var raw = storageGet(DRAFT_KEY);
    if (!raw) return 0;
    var draft;
    try { draft = JSON.parse(raw); } catch (_) { return 0; }
    if (!draft || !draft.fields || (draft.savedAt && (Date.now() - draft.savedAt) > DRAFT_TTL_MS)) { storageRemove(DRAFT_KEY); return 0; }
    Array.prototype.forEach.call(form.elements, function (el) {
      if (!el.name || el.type === "hidden" || !(el.name in draft.fields)) return;
      if (el.type === "checkbox" || el.type === "radio") {
        el.checked = Array.isArray(draft.fields[el.name]) && draft.fields[el.name].indexOf(el.value) >= 0;
      } else if (el.type !== "submit" && el.type !== "button") {
        el.value = draft.fields[el.name];
      }
    });
    var toggle = root.querySelector("[data-fhq-concierge-kids-toggle]");
    var kw = root.querySelector("[data-fhq-concierge-kids]");
    if (kw) kw.hidden = !(toggle && toggle.checked);
    return typeof draft.step === "number" ? draft.step : 0;
  }
  function clearDraft() { storageRemove(DRAFT_KEY); }

  function showStep(i, skipFieldFocus) {
    current = Math.max(0, Math.min(i, steps.length - 1));
    steps.forEach(function (s, idx) { s.hidden = idx !== current; });
    if (backBtn) backBtn.hidden = current === 0;
    var onLast = current === steps.length - 1;
    if (nextBtn) nextBtn.hidden = onLast;
    if (submitBtn) submitBtn.hidden = !onLast;
    if (progressFill) progressFill.style.width = ((current + 1) / steps.length * 100) + "%";
    if (stepCount) stepCount.textContent = "Step " + (current + 1) + " of " + steps.length;
    // On an auto-opened popup, focus the dialog rather than the first field (don't yank focus / pop a mobile keyboard).
    if (skipFieldFocus) {
      if (dialog) { dialog.setAttribute("tabindex", "-1"); dialog.focus(); }
      return;
    }
    var firstField = steps[current].querySelector("input, select, textarea");
    if (firstField) firstField.focus();
  }

  function validateStep() {
    var location = form.querySelector("[name='Location']");
    if (current === 0 && location && location.value.trim() === "") {
      setStatus("Enter a city or area.");
      if (location) location.focus();
      return false;
    }
    var email = form.querySelector("[name='email']");
    if (current === steps.length - 1 && email && !emailPattern.test(email.value.trim())) {
      setStatus("Enter a valid email address.");
      if (email) email.focus();
      return false;
    }
    setStatus("");
    return true;
  }

  function setStatus(msg) { if (statusEl) statusEl.textContent = msg || ""; }

  function open(e, isAuto) {
    if (e) e.preventDefault();
    lastFocused = document.activeElement;
    // Any open (manual or auto) records that the popup has been shown, so it never auto-opens again — the button takes over.
    storageSet(AUTOSHOWN_KEY, new Date().toISOString());
    root.classList.add("is-open");
    root.setAttribute("aria-hidden", "false");
    document.body.classList.add("fhq-concierge-open");
    trackEvent("concierge_open", { source: analyticsSource, auto: !!isAuto });
    if (storageGet(SUBMITTED_KEY)) {
      // Returning visitor — they've already used their free plan; show the account upsell, not the wizard.
      showUpsell();
    } else {
      showStep(restoreDraft(), !!isAuto);
    }
    document.addEventListener("keydown", onKeydown, true);
  }

  function close() {
    root.classList.remove("is-open");
    root.setAttribute("aria-hidden", "true");
    document.body.classList.remove("fhq-concierge-open");
    document.removeEventListener("keydown", onKeydown, true);
    if (lastFocused && typeof lastFocused.focus === "function") lastFocused.focus();
  }

  function onKeydown(e) {
    if (e.key === "Escape") {
      // If a typeahead suggestion dropdown is open, let its own (bubble-phase) handler close just the
      // dropdown — this document listener is capture-phase and would otherwise tear down the whole overlay.
      if (dialog && dialog.querySelector('[role="combobox"][aria-expanded="true"]')) return;
      close();
      return;
    }
    if (e.key !== "Tab") return;
    var focusable = dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
    var visible = Array.prototype.filter.call(focusable, function (el) { return el.offsetParent !== null; });
    if (!visible.length) return;
    var first = visible[0], last = visible[visible.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function showDone(msg) {
    if (form) form.hidden = true;
    if (upsellPanel) upsellPanel.hidden = true;
    if (donePanel) donePanel.hidden = false;
    if (doneText) doneText.textContent = msg;
    if (resendBtn) resendBtn.disabled = false;
    if (resendStatus) resendStatus.textContent = "";
  }

  // Resend: re-submits the SAME (still-populated, just hidden) form to /submit. The confirm-funnel loses real
  // people to spam-foldered/missed confirm emails, so this gives them a one-click second try without any new
  // endpoint — it reuses the existing non-revealing submit response and its per-email/day cap.
  if (resendBtn && form) resendBtn.addEventListener("click", function () {
    resendBtn.disabled = true;
    if (resendStatus) resendStatus.textContent = "Resending…";

    fetch(form.getAttribute("action") || "/activity-concierge/submit", {
      method: "POST",
      headers: { "X-Requested-With": "XMLHttpRequest" },
      body: new FormData(form),
      credentials: "same-origin"
    })
      .then(function (r) { return r.json().catch(function () { return {}; }); })
      .then(function (data) {
        var ok = !!(data && data.success);
        trackEvent("concierge_resend", { source: analyticsSource });
        if (resendStatus) resendStatus.textContent = ok
          ? "Sent again — check your inbox (and spam folder)."
          : "Couldn't resend just now. Please try again in a moment.";
        resendBtn.disabled = ok; // stays disabled once it succeeds; re-enabled so a genuine failure can be retried
      })
      .catch(function () {
        if (resendStatus) resendStatus.textContent = "Network error — please try again.";
        resendBtn.disabled = false;
      });
  });

  // Returning visitor (already submitted once): show the account upsell instead of the wizard.
  function showUpsell() {
    if (!upsellPanel) { showDone("Your plan is on its way — check your inbox."); return; }
    if (form) form.hidden = true;
    if (donePanel) donePanel.hidden = true;
    upsellPanel.hidden = false;
    trackEvent("concierge_upsell_shown", { source: analyticsSource });
    var cta = upsellPanel.querySelector("[data-fhq-concierge-upsell-cta]");
    if (cta) cta.focus();
  }

  // Launchers (there may be the floating pill + an inline hero button).
  document.querySelectorAll("[data-fhq-concierge-open]").forEach(function (btn) {
    btn.addEventListener("click", open);
  });
  root.querySelectorAll("[data-fhq-concierge-dismiss]").forEach(function (btn) {
    btn.addEventListener("click", close);
  });

  var upsellCta = root.querySelector("[data-fhq-concierge-upsell-cta]");
  if (upsellCta) upsellCta.addEventListener("click", function () {
    trackEvent("concierge_upsell_click", { source: analyticsSource });
  });

  // Holiday nudge: one tap applies the holiday's date window to the wizard.
  var holidayBtn = root.querySelector("[data-fhq-concierge-holiday]");
  if (holidayBtn && form) holidayBtn.addEventListener("click", function () {
    var win = holidayBtn.getAttribute("data-window");
    var sel = form.querySelector("[name='DateWindow']");
    if (sel && win) sel.value = win;
    holidayBtn.classList.add("is-applied");
    var txt = holidayBtn.querySelector("[data-fhq-concierge-holiday-text]");
    if (txt) txt.textContent = "Added to your plan";
    trackEvent("concierge_holiday_apply", { source: analyticsSource });
  });

  // Kid-ages reveal.
  var kidsWrap = root.querySelector("[data-fhq-concierge-kids]");
  root.querySelectorAll("input[name='Audience']").forEach(function (radio) {
    radio.addEventListener("change", function () {
      if (kidsWrap) kidsWrap.hidden = !root.querySelector("[data-fhq-concierge-kids-toggle]").checked;
    });
  });

  if (nextBtn) nextBtn.addEventListener("click", function () { if (validateStep()) { showStep(current + 1); saveDraft(); } });
  if (backBtn) backBtn.addEventListener("click", function () { showStep(current - 1); saveDraft(); });
  // Persist answers as they're entered so a close/reopen resumes where the visitor left off.
  if (form) {
    form.addEventListener("input", saveDraft);
    form.addEventListener("change", saveDraft);
  }

  // Location typeahead (city/ZIP autocomplete from the bundled dataset); a selection also fills the ZIP field.
  var locationInput = form && form.querySelector("#fhq-concierge-location");
  var zipInput = form && form.querySelector("#fhq-concierge-zip");
  try {
    if (locationInput) attachPlaceTypeahead(locationInput, { zipInput: zipInput });
    var geoBtn = form && form.querySelector("[data-fhq-concierge-geo]");
    if (geoBtn && locationInput) attachUseMyLocation(geoBtn, { locationInput: locationInput, zipInput: zipInput, onStatus: setStatus });
  } catch (_) { /* An enhancement (typeahead / geolocation) must never break the rest of the wizard or the auto-open below. */ }

  // Enter on a non-final step advances instead of submitting (the wizard is one <form>, so a bare Enter would
  // otherwise post and trip email validation). The typeahead stops propagation when it consumes Enter to pick a
  // suggestion, so this won't fire in that case; textareas keep native Enter.
  if (form) {
    form.addEventListener("keydown", function (e) {
      if (e.key !== "Enter") return;
      var t = e.target;
      if (t && (t.tagName === "TEXTAREA" || t.type === "submit")) return;
      if (current < steps.length - 1) {
        e.preventDefault();
        if (validateStep()) showStep(current + 1);
      }
    });
  }

  if (form) {
    form.addEventListener("submit", function (e) {
      e.preventDefault();
      if (!validateStep()) return;

      if (submitBtn) submitBtn.disabled = true;
      setStatus("Sending…");

      fetch(form.getAttribute("action") || "/activity-concierge/submit", {
        method: "POST",
        headers: { "X-Requested-With": "XMLHttpRequest" },
        body: new FormData(form),
        credentials: "same-origin"
      })
        .then(function (r) { return r.json().catch(function () { return {}; }); })
        .then(function (data) {
          if (data && data.success) {
            storageSet(SUBMITTED_KEY, new Date().toISOString());
            clearDraft();
            trackEvent("concierge_submit", { source: analyticsSource });
            showDone(data.message || "Check your inbox to confirm and we'll build your list.");
          } else {
            if (submitBtn) submitBtn.disabled = false;
            setStatus((data && data.error) || "Something went wrong. Please try again.");
          }
        })
        .catch(function () {
          if (submitBtn) submitBtn.disabled = false;
          setStatus("Network error. Please try again.");
        });
    });
  }

  // One-time auto-open ("popup"): on desktop, open once per visitor after light engagement, then rely on the launcher
  // button. Gated to desktop to avoid intrusive mobile interstitials (these are SEO-driven pages). Any prior open or
  // submit suppresses it (open() persists AUTOSHOWN_KEY), so returning visitors only ever see the button.
  var AUTO_DELAY_MS = 7000;
  var AUTO_SCROLL_RATIO = 0.4;
  function maybeAutoOpen() {
    if (window.innerWidth <= 640) return;
    if (storageGet(AUTOSHOWN_KEY) || storageGet(SUBMITTED_KEY)) return;
    var fired = false;
    var timer = null;
    function cleanup() {
      if (timer) window.clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    }
    function fire() {
      if (fired) return;
      fired = true;
      cleanup();
      if (root.classList.contains("is-open") || storageGet(AUTOSHOWN_KEY) || storageGet(SUBMITTED_KEY)) return;
      open(null, true);
    }
    function onScroll() {
      var max = document.documentElement.scrollHeight - window.innerHeight;
      if (max <= 0 || (window.scrollY / max) >= AUTO_SCROLL_RATIO) fire();
    }
    timer = window.setTimeout(fire, AUTO_DELAY_MS);
    window.addEventListener("scroll", onScroll, { passive: true });
  }
  maybeAutoOpen();

  // Returning-visitor nudge: once the auto-popup has already fired (this visit or a prior one) and the visitor
  // didn't submit, a brief pulse on the floating pill draws the eye back to it. Deliberately NOT inside
  // maybeAutoOpen() — that function early-returns on exactly this (autoshown, not-submitted) population, so the
  // nudge has to live alongside it, not inside it. Skipped once submitted (those visitors get the upsell panel
  // on open, not another prompt to start over).
  var NUDGE_DELAY_MS = 1500;
  var NUDGE_CLASS = "fhq-concierge-launch--nudge";
  var launchPill = document.querySelector(".fhq-concierge-launch");
  if (launchPill && storageGet(AUTOSHOWN_KEY) && !storageGet(SUBMITTED_KEY)) {
    var nudgeTimer = window.setTimeout(function () {
      // Re-check at fire time, not just at arm time: a second tab on another guide page can submit within the
      // delay window (SUBMITTED_KEY is a same-origin localStorage write, visible across tabs immediately), and
      // the dialog can open via the separate inline hero button, not just this pill.
      if (storageGet(SUBMITTED_KEY) || root.classList.contains("is-open")) return;
      launchPill.classList.add(NUDGE_CLASS);
      var clearNudgeClass = function () { launchPill.classList.remove(NUDGE_CLASS); };
      launchPill.addEventListener("animationend", clearNudgeClass, { once: true });
      // Fallback in case animationend never fires (prefers-reduced-motion suppresses the keyframe entirely).
      window.setTimeout(clearNudgeClass, 5000);
    }, NUDGE_DELAY_MS);
    launchPill.addEventListener("click", function () {
      window.clearTimeout(nudgeTimer);
      launchPill.classList.remove(NUDGE_CLASS);
    }, { once: true });
  }
})();
